import type { ApiResponse } from "../utils/schema";
import * as Text from "../i18n/en-gb.json";
import moment from "moment/src/moment";

export type GuestInfo = {
    min: number,
    max: number,
    ipm_value: number,
    allowed: boolean,
    label: string,
    helper_text?: string
};

export type PickerState = {
    isHovered: boolean
};

export type PickerStyle = {
    backgroundColor?: string,
    color?: string,
    fontWeight?: number,
    cursor?: string
};

export type State = {
    demoMode: boolean,
    clearState: boolean,
    defaultState: boolean,
    forceReload: boolean,
    selectedDate: Array<any>,
    requirements: {
        totalGuests: number,
        totalNights: number,
        capacity: {
            daily: number,
            total: number
        }
    },
    errorMessageVisible: boolean,
    errorMessageText: string,
    errorMessageClass: string,
    place: {
        id: string,
        env: "production"|"staging"|"development",
        name: string,
        url: string,
        totalCapacity: number,
        availability_cal: boolean,
        websiteUrl: string,
        bookables: Array<any>,
        currency: "GBP"|"EUR",
        api_endpoint: string,
        show_accommodation: boolean,
        suitability: {
            children: 0|1,
            babies: 0|1,
            pets: 0|1
        }
    },
    apiData: Partial<ApiResponse["calendar"]>,
    bookableCalendar: {
        availableCheckInDates: Array<any>,
        availableCheckOutDates: Array<any>,
        disabledDates: Array<any>,
        unavailableBookableDates: Array<any>
    },
    bookables: Array<any>,
    apiDates: {
        place: {
            disabledDates: Array<any>,
            availableCheckInDates: Array<any>,
            availableCheckOutDates: Array<any>,
            allBookablesAPI: Array<any>
        },
        bookables_data: Array<any>,
        minMaxDates: {
            start: moment,
            end: moment
        },
        startDateRules: {
            min: any,
            max: any
        },
        selectedDatesUnavailable: Array<any>,
        tempUnavailableDatesStore: Array<any>
    },
    maxEndDate: string,
    showBookableCalendar: number | null,
    showBookablesSection: boolean,
    showGuestWindow: boolean,
    showCalendar: boolean,
    showKeyCheckInOut: boolean,
    showKeyOnRequest: boolean,
    calendarTitle: string,
    calendarText: string,
    calendarTextCheckIn: string,
    calendarTextCheckOut: string,
    calendarTextSeperator: {
        medium: string,
        small: string
    },
    calendarHasDoublePane: boolean,
    calendarPaginationKey: boolean,
    keyCheckInOutText: string,
    bookableCalendarPaginationKey: null | {year: number, month: number},
    dateSelectionComplete: boolean,
    totalNightsSelected: string,
    isLoading: boolean,
    pickerAttributes: Array<any>,
    bookableAttributes: Array<any>,
    guestSelector: {
        selector: {
            adults: GuestInfo,
            children: GuestInfo,
            babies: GuestInfo,
            pets: GuestInfo
        },
        ipm_slot_incr: string,
        ipm_slot_decr: string,
        guestInputValue: string,
        guestsTotal: number,
    },
    pickerStyles: { // Don't forget to update the style guide in this modules readme.md if you change anything below
        today: {
            key: string,
            highlight: {
                borderRadius: string
            },
            contentStyle: (state: PickerState) => PickerStyle,
            dates: Date,
        },
        disabledDates: (state: PickerState) => PickerStyle,
        availableDates: (state: PickerState) => PickerStyle,
        availableDatesBookable: (state: PickerState) => PickerStyle,
        availableNonSelectableDates: (state: PickerState) => PickerStyle,
        requestDates: (state: PickerState) => PickerStyle,
        checkInOutBar: PickerStyle,
        selectedDates: PickerStyle
    }
};

const state: State = {
    demoMode: false,
    selectedDate: [],
    clearState: false,
    defaultState: false,
    requirements: { // Stores guest and dates selection
        totalGuests: 2, // Now start with 2 adults as default
        totalNights: 0,
        capacity: {
            daily: 0,
            total: 0
        }
    },
    errorMessageVisible: false,
    errorMessageText: 'You must enter guests and dates to send an enquiry.',
    errorMessageClass: 'ac-checker-error',
    // Provide sensible default values
    place: {
        id: "", // Place IDs sometimes contain letters
        env: "production",
        name: "",
        url: "",
        totalCapacity: 0,
        currency: "GBP",
        availability_cal: false,
        websiteUrl: "",
        bookables: [],
        api_endpoint: "",
        show_accommodation: false,
        suitability: {
            children: 0,
            babies: 0,
            pets: 0
        }
    },
    apiData: {},
    bookableCalendar: {
        availableCheckInDates : [],
        availableCheckOutDates : [],
        disabledDates : [],
        unavailableBookableDates: []
    },
    bookables: [],
    apiDates: {
        place: {
            disabledDates: [],
            availableCheckInDates: [],
            availableCheckOutDates: [],
            allBookablesAPI: []
        },
        bookables_data: [],
        minMaxDates: {
            start: moment().add(1, 'days'),
            end : moment().add(18, 'months')
        },
        startDateRules: {
            min: undefined,
            max: undefined
        },
        selectedDatesUnavailable: [],
        tempUnavailableDatesStore: []
    },
    maxEndDate: '',
    showBookableCalendar: null,
    showBookablesSection: false,
    showGuestWindow: false,
    showCalendar: false,
    showKeyCheckInOut: false,
    showKeyOnRequest: false,
    calendarTitle: Text.calendar.title,
    calendarText: Text.calendar.text,
    calendarTextCheckIn: Text.calendar.checkIn,
    calendarTextCheckOut: Text.calendar.checkOut,
    calendarTextSeperator: {
        medium: '   -   ',
        small: '  -  '
    },
    calendarHasDoublePane: false,
    calendarPaginationKey: false,
    keyCheckInOutText: 'Check in day',
    bookableCalendarPaginationKey: null,
    dateSelectionComplete: false,
    totalNightsSelected: '',
    isLoading: false,
    pickerAttributes: [],
    bookableAttributes: [],
    guestSelector: {
        selector: {
            adults: {
                min: 0,
                max: 100,
                ipm_value: 2,
                allowed: true,
                label: 'Adults'
            },
            children: {
                min: 0,
                max: 100,
                ipm_value: 0,
                helper_text: 'Ages 2+',
                allowed: false,
                label: 'Children'
            },
            babies: {
                min: 0,
                max: 5,
                ipm_value: 0,
                helper_text: 'Ages 0-2',
                allowed: false,
                label: 'Babies'
            },
            pets: {
                min: 0,
                max: 5,
                ipm_value: 0,
                allowed: false,
                label: 'Pets'
            }
        },
        ipm_slot_incr: '+',
        ipm_slot_decr: '-',
        guestInputValue: '2 Adults',
        guestsTotal: 0
    },
    pickerStyles: { // Don't forget to update the style guide in this modules readme.md if you change anything below
        today: {
            key: 'today',
            highlight: {
                borderRadius: '0',
            },
            contentStyle: ({ isHovered }: { isHovered: boolean }) => ({
                backgroundColor: '#FFFFFF',
                color: '#252525',
                fontWeight: 400,
                ...(isHovered && {
                    cursor: 'not-allowed'
                })
            }),
            dates: new Date(),
        },
        disabledDates: ({ isHovered }: { isHovered: boolean }) => ({
            backgroundColor: '#F2F2F2',
            color: '#BFBFBF',
            fontWeight: 400,
            ...(isHovered && {
                cursor: 'not-allowed'
            })
        }),
        availableDates: ({ isHovered }: { isHovered: boolean }) => ({
            backgroundColor: '#FFFFFF',
            color: '#252525',
            fontWeight: 400,
            ...(isHovered && {
                backgroundColor: '#F3BD47',
                cursor: 'pointer'
            })
        }),
        availableDatesBookable: ({ isHovered }: { isHovered: boolean }) => ({
            backgroundColor: '#FFFFFF',
            color: '#252525',
            fontWeight: 400,
            ...(isHovered && {
                cursor: 'default'
            })
        }),
        availableNonSelectableDates: ({ isHovered }: { isHovered: boolean }) => ({
            backgroundColor: '#FFFFFF',
            color: '#252525',
            fontWeight: 400,
            ...(isHovered && {
                cursor: 'not-allowed'
            })
        }),
        requestDates: ({ isHovered }: { isHovered: boolean }) => ({
            backgroundColor: '#0E085F',
            color: '#FFFFFF',
            fontWeight: 400,
            ...(isHovered && {
                backgroundColor: '#F3BD47',
                cursor: 'pointer'
            })
        }),
        checkInOutBar: {
            backgroundColor: '#2EA29A',
        },
        selectedDates: {
            backgroundColor: '#2EA29A',
            color: '#FFFFFF',
            fontWeight: 400,
        },
    },
    forceReload: false
};

export default state;
